import React, { useState, useEffect, useRef } from 'react';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import axios from 'axios';
import { useCookies } from 'react-cookie';
import axiosPost from '../../../helpers/axios-helper';
import config from '../../../constants/config';
import RichTextEditor from '../../../components/editor/RichTextEditor';
import { slugify } from '../../../constants/venueTypes';

/**
 * LGBTQ+ organisations shown on each country page.
 * Every change sends the login token; the API refuses it otherwise.
 */

const EMPTY = {
  id: 0,
  country_id: '',
  name: '',
  focus: '',
  city: '',
  summary: '',
  description: '',
  address: '',
  images: [],
  logo: '',
  website: '',
  instagram: '',
  sort_order: 0,
  active: 1,
};

const FOCUS_SUGGESTIONS = ['Advocacy', 'Health', 'Youth', 'Legal', 'Community', 'Culture', 'Support'];

export default function Organisations() {
  const [cookies] = useCookies(['auth_token']);
  const token = cookies.auth_token;
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filterCountry, setFilterCountry] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(EMPTY);
  const [saveError, setSaveError] = useState('');
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef(null);
  const photosRef = useRef(null);

  const load = () => {
    axiosPost('organisations/list', { 'Content-Type': 'application/json' }, { token }, (resp) => {
      if (resp?.success) {
        setItems(resp.data.items || []);
        setSaveError('');
      } else {
        setSaveError(resp?.response || 'Could not load organisations.');
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    load();
    axiosPost('dashboard/countries/list', {}, {}, (resp) => {
      setCountries(Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : []));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (patch) => setFormData(prev => ({ ...prev, ...patch }));

  const countryName = (id) => countries.find(c => Number(c.id) === Number(id))?.name || '-';

  const handleAddNew = () => {
    setFormData({ ...EMPTY, country_id: filterCountry });
    setSaveError('');
    setIsEditing(true);
  };

  const handleEdit = (org) => {
    setFormData({ ...EMPTY, ...org, images: Array.isArray(org.images) ? org.images : [] });
    setSaveError('');
    setIsEditing(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this organisation? This cannot be undone.')) return;
    axiosPost('organisations/delete', { 'Content-Type': 'application/json' }, { token, id }, (resp) => {
      if (resp?.success) load();
      else setSaveError(resp?.response || 'Could not delete that organisation.');
    });
  };

  // Photos for the organisation's own page; the first one leads the gallery
  const uploadPhotos = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setUploading(true);
    setSaveError('');
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append('image', file);
        fd.append('token', token || '');
        const resp = await axios.post(`${config.api.url}organisations/upload-logo`, fd, {
          headers: { api: config.api.key, 'Content-Type': 'multipart/form-data' },
        });
        if (resp?.data?.success) {
          const url = resp.data.data.url;
          setFormData(prev => ({ ...prev, images: [...(prev.images || []), url] }));
        } else {
          setSaveError(resp?.data?.response || 'A photo upload was refused.');
        }
      }
    } catch (err) {
      setSaveError('Could not upload that photo.');
    } finally {
      setUploading(false);
    }
  };

  const movePhoto = (i, dir) => setFormData(prev => {
    const imgs = [...(prev.images || [])];
    const j = i + dir;
    if (j < 0 || j >= imgs.length) return prev;
    [imgs[i], imgs[j]] = [imgs[j], imgs[i]];
    return { ...prev, images: imgs };
  });

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('token', token || '');
      const resp = await axios.post(`${config.api.url}organisations/upload-logo`, fd, {
        headers: { api: config.api.key, 'Content-Type': 'multipart/form-data' },
      });
      if (resp?.data?.success) set({ logo: resp.data.data.url });
      else setSaveError(resp?.data?.response || 'The upload was refused.');
    } catch (err) {
      setSaveError('Could not upload that image.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaveError('');

    if (!formData.country_id || !formData.name.trim()) {
      setSaveError('Choose a country and enter a name.');
      return;
    }

    const payload = {
      ...formData,
      token,
      country_id: parseInt(formData.country_id, 10),
      sort_order: parseInt(formData.sort_order || 0, 10),
    };
    axiosPost('organisations/update', { 'Content-Type': 'application/json' }, payload, (resp) => {
      if (resp?.success) {
        setIsEditing(false);
        load();
      } else {
        setSaveError(resp?.response || 'Could not save that organisation.');
      }
    });
  };

  const visible = filterCountry
    ? items.filter(o => Number(o.country_id) === Number(filterCountry))
    : items;

  if (isLoading) return <div>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>LGBTQ+ Organisations</h2>
        {!isEditing && (
          <div style={{ display: 'flex', gap: 10 }}>
            <select
              className="DashboardFormInput"
              style={{ width: 'auto' }}
              value={filterCountry}
              onChange={(e) => setFilterCountry(e.target.value)}
              aria-label="Filter by country"
            >
              <option value="">All countries</option>
              {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button onClick={handleAddNew} className="DashboardButton"><FaPlus /> Add Organisation</button>
          </div>
        )}
      </div>

      {saveError && <p style={{ color: '#d32f2f' }}>{saveError}</p>}

      {isEditing ? (
        <form onSubmit={handleSubmit}>
          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Country</label>
            <select
              className="DashboardFormInput"
              value={formData.country_id}
              onChange={(e) => set({ country_id: e.target.value })}
            >
              <option value="">Choose a country</option>
              {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Name</label>
            <input type="text" className="DashboardFormInput" value={formData.name} onChange={(e) => set({ name: e.target.value })} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Focus</label>
              <input
                type="text"
                list="org-focus-options"
                className="DashboardFormInput"
                value={formData.focus}
                onChange={(e) => set({ focus: e.target.value })}
                placeholder="e.g. Advocacy"
              />
              <datalist id="org-focus-options">
                {FOCUS_SUGGESTIONS.map(f => <option key={f} value={f} />)}
              </datalist>
            </div>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">City</label>
              <input type="text" className="DashboardFormInput" value={formData.city} onChange={(e) => set({ city: e.target.value })} />
            </div>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Order</label>
              <input type="number" className="DashboardFormInput" value={formData.sort_order} onChange={(e) => set({ sort_order: e.target.value })} />
            </div>
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Summary</label>
            <textarea className="DashboardFormInput" rows={4} value={formData.summary} onChange={(e) => set({ summary: e.target.value })} />
            <small style={{ color: '#666' }}>Short text on the card on the country page.</small>
          </div>

          <h3>Organisation page</h3>
          <p style={{ color: '#666', marginTop: 0 }}>
            Clicking the card on the country page opens this page, laid out like a hotel page.
            {formData.id ? <> Live at <a href={`/organisation/${formData.id}-${slugify(formData.name)}`} target="_blank" rel="noreferrer">/organisation/{formData.id}-{slugify(formData.name)}</a>.</> : null}
          </p>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Address</label>
            <input type="text" className="DashboardFormInput" value={formData.address || ''} onChange={(e) => set({ address: e.target.value })} />
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">About the organisation</label>
            <RichTextEditor content={formData.description || ''} onChange={(html) => set({ description: html })} placeholder="Their story, their work, how visitors can support them..." minHeight={200} />
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Photos</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
              {(formData.images || []).map((url, i) => (
                <div key={url + i} style={{ width: 140 }}>
                  <img src={url} alt="" style={{ width: 140, height: 95, objectFit: 'cover', display: 'block', border: i === 0 ? '2px solid #2c2c2c' : '1px solid #ddd' }} />
                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    <button type="button" className="DashboardButtonSecondary" onClick={() => movePhoto(i, -1)} disabled={i === 0} aria-label="Move left">←</button>
                    <button type="button" className="DashboardButtonSecondary" onClick={() => movePhoto(i, 1)} disabled={i === formData.images.length - 1} aria-label="Move right">→</button>
                    <button type="button" className="DashboardButtonSecondary" onClick={() => set({ images: formData.images.filter((_, idx) => idx !== i) })}>✕</button>
                  </div>
                </div>
              ))}
            </div>
            <input ref={photosRef} type="file" multiple accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ''; }} />
            <button type="button" className="DashboardButtonSecondary" disabled={uploading} onClick={() => photosRef.current.click()}>
              {uploading ? 'Uploading...' : 'Add photos'}
            </button>
            <small style={{ color: '#666', marginLeft: 8 }}>The first photo (outlined) is the main one.</small>
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Logo</label>
            {formData.logo && (
              <div style={{ marginBottom: 8 }}>
                <img src={formData.logo} alt="" style={{ maxWidth: 120, maxHeight: 80, objectFit: 'contain' }} />
              </div>
            )}
            <input ref={logoRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={(e) => { uploadLogo(e.target.files[0]); e.target.value = ''; }} />
            <button type="button" className="DashboardButtonSecondary" disabled={uploading} onClick={() => logoRef.current.click()} style={{ marginRight: 8 }}>
              {uploading ? 'Uploading...' : 'Upload logo'}
            </button>
            {formData.logo && (
              <button type="button" className="DashboardButtonSecondary" onClick={() => set({ logo: '' })}>Remove</button>
            )}
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Website</label>
            <input type="text" className="DashboardFormInput" value={formData.website} onChange={(e) => set({ website: e.target.value })} placeholder="https://" />
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel">Instagram</label>
            <input type="text" className="DashboardFormInput" value={formData.instagram} onChange={(e) => set({ instagram: e.target.value })} placeholder="https://instagram.com/..." />
          </div>

          <div className="DashboardFormGroup">
            <label className="DashboardFormLabel" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={!!formData.active} onChange={(e) => set({ active: e.target.checked ? 1 : 0 })} />
              Show on the site
            </label>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" className="DashboardButtonSecondary" onClick={() => setIsEditing(false)}>Cancel</button>
            <button type="submit" className="DashboardButton" disabled={uploading}>
              {formData.id ? 'Update Organisation' : 'Add Organisation'}
            </button>
          </div>
        </form>
      ) : (
        <table className="DashboardTable">
          <thead>
            <tr>
              <th>Name</th>
              <th>Country</th>
              <th>Focus</th>
              <th>Status</th>
              <th>Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={6} style={{ color: '#667085' }}>No organisations yet.</td>
              </tr>
            )}
            {visible.map((org) => (
              <tr key={org.id}>
                <td>{org.name}</td>
                <td>{countryName(org.country_id)}</td>
                <td>{org.focus || '-'}</td>
                <td>{org.active ? 'Live' : 'Hidden'}</td>
                <td>{org.sort_order}</td>
                <td>
                  <button onClick={() => handleEdit(org)} className="DashboardButtonSecondary" style={{ marginRight: 8 }}><FaEdit /> Edit</button>
                  <button onClick={() => handleDelete(org.id)} className="DashboardButtonSecondary" style={{ color: '#d32f2f' }}><FaTrash /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
